import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { isEqual } from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { TopicData, useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import {
  Entity,
  VectorFieldDisplayMode,
} from "@/shared/state/visualization/schema/v1/three-d";

import { DataSourceSelector } from "../../../controls/DataSourceSelector";
import { isViewableAsPlot } from "../../../Panel/predicates";
import { ConfigItem } from "../ConfigItem";

import styles from "./EntityConfig.module.css";
import {
  getFileAndPath,
  topicDataFor,
} from "./PositionConfig/positionTopicData";
import {
  buildVectorFieldSpec,
  parseNumberInput,
  validateStride,
} from "./vectorFieldValidation";

const DEFAULT_COLOR = "#e85d3a";
const DEFAULT_GRADIENT_LOW = "#0000ff";
const DEFAULT_GRADIENT_HIGH = "#ff0000";

interface Props {
  entity: Entity;
  panelId: string;
}

export function VectorFieldConfig({ entity, panelId }: Props) {
  const dispatch = useVizDispatch();

  // Track the most recent vectorField, inclusive of updates originating from this component.
  const vectorFieldRef = React.useRef(entity.vectorField);

  // A key used to reload the data source selectors to match external updates.
  const reloadKeyRef = React.useRef(0);

  // Data source state
  const [forwardData, setForwardData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.vectorField?.forward),
  );
  const [rightData, setRightData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.vectorField?.right),
  );
  const [upData, setUpData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.vectorField?.up),
  );

  // Scale state
  const [forwardScale, setForwardScale] = React.useState(
    `${entity.vectorField?.forwardScale ?? 1}`,
  );
  const [forwardScaleError, setForwardScaleError] = React.useState("");
  const [rightScale, setRightScale] = React.useState(
    `${entity.vectorField?.rightScale ?? 1}`,
  );
  const [rightScaleError, setRightScaleError] = React.useState("");
  const [upScale, setUpScale] = React.useState(
    `${entity.vectorField?.upScale ?? 1}`,
  );
  const [upScaleError, setUpScaleError] = React.useState("");

  // Display state
  const [lineWidth, setLineWidth] = React.useState(
    `${entity.vectorField?.lineWidth ?? 2}`,
  );
  const [lineWidthError, setLineWidthError] = React.useState("");
  const [color, setColor] = React.useState(
    entity.vectorField?.color || DEFAULT_COLOR,
  );
  const [stride, setStride] = React.useState(
    `${entity.vectorField?.stride ?? 1}`,
  );
  const [strideError, setStrideError] = React.useState("");
  const [displayMode, setDisplayMode] = React.useState<VectorFieldDisplayMode>(
    entity.vectorField?.displayMode ?? "current_only",
  );
  const [gradientEnabled, setGradientEnabled] = React.useState(
    entity.vectorField?.gradientEnabled ?? false,
  );
  const [colorLow, setColorLow] = React.useState(
    entity.vectorField?.colorLow || DEFAULT_GRADIENT_LOW,
  );
  const [colorHigh, setColorHigh] = React.useState(
    entity.vectorField?.colorHigh || DEFAULT_GRADIENT_HIGH,
  );

  const forwardConfig = getFileAndPath(entity.vectorField?.forward);
  const rightConfig = getFileAndPath(entity.vectorField?.right);
  const upConfig = getFileAndPath(entity.vectorField?.up);

  React.useEffect(
    function onFieldsChanged() {
      if (!isEqual(vectorFieldRef.current, entity.vectorField)) {
        // entity.vectorField was changed without this component's involvement.
        vectorFieldRef.current = entity.vectorField;
        setForwardScale(`${entity.vectorField?.forwardScale ?? 1}`);
        setRightScale(`${entity.vectorField?.rightScale ?? 1}`);
        setUpScale(`${entity.vectorField?.upScale ?? 1}`);
        setLineWidth(`${entity.vectorField?.lineWidth ?? 2}`);
        setColor(entity.vectorField?.color || DEFAULT_COLOR);
        setStride(`${entity.vectorField?.stride ?? 1}`);
        setDisplayMode(entity.vectorField?.displayMode ?? "current_only");
        setGradientEnabled(entity.vectorField?.gradientEnabled ?? false);
        setColorLow(entity.vectorField?.colorLow || DEFAULT_GRADIENT_LOW);
        setColorHigh(entity.vectorField?.colorHigh || DEFAULT_GRADIENT_HIGH);

        setForwardData(topicDataFor(entity.vectorField?.forward));
        setRightData(topicDataFor(entity.vectorField?.right));
        setUpData(topicDataFor(entity.vectorField?.up));
        reloadKeyRef.current++;
        return;
      }

      const parsedForwardScale = parseNumberInput(
        forwardScale,
        "Forward scale",
      );
      const parsedRightScale = parseNumberInput(rightScale, "Right scale");
      const parsedUpScale = parseNumberInput(upScale, "Up scale");
      const parsedLineWidth = parseNumberInput(lineWidth, "Line width");
      const parsedStride = parseNumberInput(stride, "Stride");

      setForwardScaleError(parsedForwardScale.error ?? "");
      setRightScaleError(parsedRightScale.error ?? "");
      setUpScaleError(parsedUpScale.error ?? "");
      setLineWidthError(parsedLineWidth.error ?? "");
      setStrideError(parsedStride.error ?? "");

      if (
        !parsedForwardScale.ok ||
        !parsedRightScale.ok ||
        !parsedUpScale.ok ||
        !parsedLineWidth.ok ||
        !parsedStride.ok
      ) {
        return;
      }

      const strideValidationError = validateStride(parsedStride.value);
      if (strideValidationError) {
        setStrideError(strideValidationError);
        return;
      }

      const updated = buildVectorFieldSpec(
        {
          forwardData,
          rightData,
          upData,
          forwardScale: parsedForwardScale.value,
          rightScale: parsedRightScale.value,
          upScale: parsedUpScale.value,
          lineWidth: parsedLineWidth.value,
          stride: parsedStride.value,
          color,
          displayMode,
          gradientEnabled,
          colorLow,
          colorHigh,
        },
        entity.vectorField,
      );

      if (updated && !isEqual(updated, entity.vectorField)) {
        vectorFieldRef.current = updated;
        dispatch(
          updateThreeDEntity(panelId, entity.id, { vectorField: updated }),
        );
      }
    },
    [
      forwardData,
      rightData,
      upData,
      forwardScale,
      rightScale,
      upScale,
      lineWidth,
      color,
      stride,
      displayMode,
      gradientEnabled,
      colorLow,
      colorHigh,
      entity.vectorField,
      entity.id,
      panelId,
      dispatch,
    ],
  );

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        Vector Field
      </RobotoTypography>
      <div className={styles.container}>
        <div key={reloadKeyRef.current} className={styles.axisGroup}>
          <ConfigItem label="Right Axis">
            <DataSourceSelector
              initialFile={rightConfig?.fileId}
              initialPath={rightConfig?.path}
              onChange={(node) => setRightData(node.toTopicData())}
              optionFilter={isViewableAsPlot}
            />
          </ConfigItem>
          <ConfigItem label="Forward Axis">
            <DataSourceSelector
              initialFile={forwardConfig?.fileId}
              initialPath={forwardConfig?.path}
              onChange={(node) => setForwardData(node.toTopicData())}
              optionFilter={isViewableAsPlot}
            />
          </ConfigItem>
          <ConfigItem label="Up Axis">
            <DataSourceSelector
              initialFile={upConfig?.fileId}
              initialPath={upConfig?.path}
              onChange={(node) => setUpData(node.toTopicData())}
              optionFilter={isViewableAsPlot}
            />
          </ConfigItem>
        </div>
        <ConfigItem label="Scaling">
          <div className={styles.fieldRow}>
            <TextField
              label="Right Scale"
              type="number"
              size="small"
              margin="dense"
              value={rightScale}
              error={!!rightScaleError}
              helperText={rightScaleError}
              onChange={(e) => setRightScale(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ step: 0.1, className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Forward Scale"
              type="number"
              size="small"
              margin="dense"
              value={forwardScale}
              error={!!forwardScaleError}
              helperText={forwardScaleError}
              onChange={(e) => setForwardScale(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ step: 0.1, className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Up Scale"
              type="number"
              size="small"
              margin="dense"
              value={upScale}
              error={!!upScaleError}
              helperText={upScaleError}
              onChange={(e) => setUpScale(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ step: 0.1, className: styles.input }}
              className={styles.scaleField}
            />
          </div>
        </ConfigItem>
        <ConfigItem label="Display">
          <FormControl
            size="small"
            fullWidth
            className={`${styles.field} ${styles.displayControl}`}
          >
            <InputLabel
              id={`vf-display-mode-label-${entity.id}`}
              className={styles.input}
            >
              Display Mode
            </InputLabel>
            <Select
              label="Display Mode"
              labelId={`vf-display-mode-label-${entity.id}`}
              value={displayMode}
              onChange={(e) =>
                setDisplayMode(e.target.value as VectorFieldDisplayMode)
              }
              className={styles.input}
              inputProps={{ className: styles.input }}
            >
              <MenuItem value="current_only">
                Display current vector only
              </MenuItem>
              <MenuItem value="field_only">
                Display all vectors over time
              </MenuItem>
            </Select>
          </FormControl>
          <div className={styles.fieldRow}>
            <TextField
              label="Line Width"
              type="number"
              size="small"
              margin="dense"
              value={lineWidth}
              error={!!lineWidthError}
              helperText={lineWidthError}
              onChange={(e) => setLineWidth(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 0, step: 1, className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Color"
              type="color"
              size="small"
              margin="dense"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Stride"
              type="number"
              size="small"
              margin="dense"
              value={stride}
              error={!!strideError}
              helperText={strideError}
              onChange={(e) => setStride(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 1, step: 1, className: styles.input }}
              className={styles.scaleField}
            />
          </div>
        </ConfigItem>
        <ConfigItem label="Gradient">
          <div className={styles.fieldRow}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={gradientEnabled}
                  onChange={(e) => setGradientEnabled(e.target.checked)}
                  size="small"
                />
              }
              label="Enable gradient"
              className={styles.input}
            />
          </div>
          {gradientEnabled && (
            <div className={styles.fieldRow}>
              <TextField
                label="Low Color"
                type="color"
                size="small"
                margin="dense"
                value={colorLow}
                onChange={(e) => setColorLow(e.target.value)}
                InputLabelProps={{ className: styles.input }}
                InputProps={{ className: styles.input }}
                inputProps={{ className: styles.input }}
                className={styles.scaleField}
              />
              <TextField
                label="High Color"
                type="color"
                size="small"
                margin="dense"
                value={colorHigh}
                onChange={(e) => setColorHigh(e.target.value)}
                InputLabelProps={{ className: styles.input }}
                InputProps={{ className: styles.input }}
                inputProps={{ className: styles.input }}
                className={styles.scaleField}
              />
            </div>
          )}
        </ConfigItem>
      </div>
    </>
  );
}
